import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AdminShell from "@/components/AdminShell";
import { useAuth, formatApiErrorDetail } from "@/lib/auth";
import { Save, Eye, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = {
  title: "",
  excerpt: "",
  cover_image: "",
  content: "## Heading\n\nStart writing in **markdown**...",
  tags: "",
  published: true,
};

export default function AdminPostEditor() {
  const { id } = useParams();
  const { authHeader } = useAuth();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/posts`, { headers: authHeader });
        const post = data.find((p) => p.id === id);
        if (!post) {
          toast.error("Post not found");
          navigate("/admin/posts", { replace: true });
          return;
        }
        setForm({
          ...post,
          tags: (post.tags || []).join(", "),
          excerpt: post.excerpt || "",
          cover_image: post.cover_image || "",
        });
      } catch {
        toast.error("Failed to load post.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const update = (k) => (e) => {
    const val = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((s) => ({ ...s, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      tags: form.tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      excerpt: form.excerpt || null,
      cover_image: form.cover_image || null,
    };
    try {
      if (isEdit) {
        await axios.put(`${API}/admin/posts/${id}`, payload, { headers: authHeader });
        toast.success("Post updated.");
      } else {
        await axios.post(`${API}/admin/posts`, payload, { headers: authHeader });
        toast.success("Post published.");
      }
      navigate("/admin/posts");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminShell title="Loading...">
        <div className="font-mono-ui text-[11px] tracking-[0.22em] uppercase text-[var(--text-3)]">Loading post...</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title={isEdit ? "Edit post" : "New post"}
      action={
        <div className="flex items-center gap-2">
          <button
            data-testid="post-preview-toggle"
            type="button"
            onClick={() => setShowPreview((s) => !s)}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[var(--border-dim)] hover:border-[var(--lime)] rounded-full text-xs tracking-wide transition-colors"
          >
            <Eye className="w-4 h-4" /> {showPreview ? "Edit" : "Preview"}
          </button>
          <button
            data-testid="admin-post-back"
            type="button"
            onClick={() => navigate("/admin/posts")}
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-[var(--border-dim)] rounded-full text-xs tracking-wide transition-colors hover:border-[var(--text-1)]"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      }
    >
      <form data-testid="admin-post-form" onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-5">
          <div>
            <label className="overline block mb-2">Title</label>
            <input
              data-testid="post-title-input"
              required
              value={form.title}
              onChange={update("title")}
              placeholder="A bold, scannable headline"
              className="w-full bg-[var(--bg-2)] border border-[var(--border-dim)] rounded-sm px-4 py-3 font-display text-2xl focus:outline-none focus:border-[var(--vermilion)] transition-colors"
            />
          </div>

          <div>
            <label className="overline block mb-2">Excerpt</label>
            <textarea
              data-testid="post-excerpt-input"
              rows={2}
              value={form.excerpt}
              onChange={update("excerpt")}
              placeholder="A 1-2 line summary..."
              className="w-full bg-[var(--bg-2)] border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm resize-none focus:outline-none focus:border-[var(--vermilion)] transition-colors"
            />
          </div>

          <div>
            <label className="overline block mb-2">Content (Markdown)</label>
            {showPreview ? (
              <div className="prose prose-invert max-w-none border border-[var(--border-dim)] bg-[var(--bg-2)] rounded-sm px-6 py-6 min-h-[400px]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.content}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                data-testid="post-content-input"
                rows={18}
                required
                value={form.content}
                onChange={update("content")}
                className="w-full bg-[var(--bg-2)] border border-[var(--border-dim)] rounded-sm px-4 py-3 font-mono-ui text-sm resize-y focus:outline-none focus:border-[var(--vermilion)] transition-colors"
              />
            )}
          </div>
        </div>

        <aside className="lg:col-span-4 space-y-5">
          <div className="border border-[var(--border-dim)] bg-[var(--bg-2)] rounded-sm p-5 space-y-5">
            <div>
              <label className="overline block mb-2">Cover image URL</label>
              <input
                data-testid="post-cover-input"
                value={form.cover_image}
                onChange={update("cover_image")}
                placeholder="https://..."
                className="w-full bg-[var(--bg)]/60 border border-[var(--border-dim)] rounded-sm px-3 py-2 font-mono-ui text-xs focus:outline-none focus:border-[var(--vermilion)]"
              />
            </div>

            <div>
              <label className="overline block mb-2">Tags (comma-separated)</label>
              <input
                data-testid="post-tags-input"
                value={form.tags}
                onChange={update("tags")}
                placeholder="design, engineering"
                className="w-full bg-[var(--bg)]/60 border border-[var(--border-dim)] rounded-sm px-3 py-2 font-mono-ui text-xs focus:outline-none focus:border-[var(--vermilion)]"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                data-testid="post-published-toggle"
                type="checkbox"
                checked={!!form.published}
                onChange={update("published")}
                className="accent-[var(--vermilion)]"
              />
              <span className="font-mono-ui text-[11px] tracking-[0.22em] uppercase">Published</span>
            </label>

            <button
              type="submit"
              data-testid="post-save-button"
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[var(--vermilion)] hover:bg-[var(--vermilion-2)] disabled:opacity-60 text-white text-sm font-medium rounded-full transition-colors"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving..." : isEdit ? "Update post" : "Publish post"}
            </button>
          </div>
        </aside>
      </form>
    </AdminShell>
  );
}
